import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ElementChangeEventModel } from '../models/stripe/element-change-event.model';
import { StripeService } from '../services/payments/stripe.service';
import { PaymentData, PaymentIntentResponse, PaymentStatusResponse } from '../models/stripe/payment.model';
import { DonationType, PaymentConstants } from './payment.constants';

declare global {
  interface Window {
    Stripe?: any;
  }
}

@Component({
  selector: 'app-stripe-card-form',
  templateUrl: './stripe-card-form.component.html',
  styleUrls: ['./stripe-card-form.component.scss']
})
export class StripeCardFormComponent implements OnInit, OnDestroy {


  // Must be true if this donation is anonymous and does not require a registered user. Default: false.
  @Input() isUnloggedUser = false;
  // If true, card form validation is bypassed because we already have verified card information.
  @Input() hasPaymentMethod = false;
  // If true, card form and external form must be valid to enable payment button. Also, externalFormValidator needs to be set.
  @Input() hasExternalValidation = false;
  // External validator in case more fields or requirements need to be fulfilled to enable payment button.
  @Input() set externalFormValidator(value: boolean) {
    if (this.cardFormGroup) {
      this.isExternalFormValid = value;
      this.formValid$.next(this.cardFormGroup.valid && this.isExternalFormValid);
    }
  }
  // Type of donations offered by the SaaS.
  @Input() donationType: DonationType;
  // External event that triggers payment process.
  @Input() paymentButtonClicked: EventEmitter<PaymentData>;
  // Event that informs parent component whether internal card form is valid.
  @Output() formValid$ = new EventEmitter<boolean>();
  // Event that informs if the payment process has succeeded or not.
  @Output() paymentStatus$ = new EventEmitter<string>();

  cardFormGroup: UntypedFormGroup;
  cardNumber: any;
  cardCvc: any;
  cardExp: any;
  CARD_NUMBER_FIELD_NAME = 'cardNumber';
  CARD_CVC_FIELD_NAME = 'cardCvc';
  CARD_EXP_FIELD_NAME = 'cardExp';
  cardNumberError: string;
  cvcError: string;
  expirationDateError: string;
  isLoading = false;

  private readonly STRIPE!: any;
  private elementStripe!: any;
  private subscriptions = new Subscription();
  private isExternalFormValid = false;

  constructor(private formBuilder: UntypedFormBuilder,
              private translate: TranslateService,
              private stripeService: StripeService) {
    this.STRIPE = window.Stripe(environment.stripePk);
  }

  async startLoggedPayment(data: PaymentData): Promise<any> {
    this.isLoading = true;

    const tokenObject = this.hasPaymentMethod ? {token: {id: ''}} : await this.STRIPE.createToken(this.cardNumber);
    this.subscriptions.add(
      this.stripeService.sendPaymentIntent(this.isUnloggedUser, tokenObject.token.id, data.amount, this.donationType, data.metadata)
        .subscribe((intentResponse: PaymentIntentResponse) => {
          this.STRIPE.handleCardPayment(intentResponse.client_secret)
            .then((stripeResponse: any) => {
              if (stripeResponse.paymentIntent) {
                this.stripeService.getPaymentStatus(stripeResponse.paymentIntent.id, this.donationType)
                  .subscribe((paymentResponse: PaymentStatusResponse) => {
                    let status: string = PaymentConstants.DONATION_STATUS_FAILED;

                    if (paymentResponse.status) {
                      if (paymentResponse.status === PaymentConstants.DONATION_STATUS_SUCCEEDED && intentResponse?.premium) {
                        status = PaymentConstants.DONATION_STATUS_PREMIUM;
                      } else if (paymentResponse.status) {
                        status = paymentResponse.status;
                      }
                    }
                    this.endPaymentProcess(status);
                  }, () => {
                    this.endPaymentProcess(PaymentConstants.DONATION_STATUS_FAILED);
                  });
              } else {
                this.endPaymentProcess(PaymentConstants.DONATION_STATUS_FAILED);
              }
            });
        }, () => {
          this.endPaymentProcess(PaymentConstants.DONATION_STATUS_FAILED);
        }));
  }

  ngOnInit(): void {
    this.cardFormGroup = this.formBuilder.group({
      [this.CARD_NUMBER_FIELD_NAME]: [false, [Validators.required, Validators.requiredTrue]],
      [this.CARD_EXP_FIELD_NAME]: [false, [Validators.required, Validators.requiredTrue]],
      [this.CARD_CVC_FIELD_NAME]: [false, [Validators.required, Validators.requiredTrue]],
    });

    if (!this.hasPaymentMethod) {
      this.subscriptions.add(
        this.cardFormGroup.valueChanges.subscribe(() => {
          if (this.hasExternalValidation) {
            this.formValid$.next(this.cardFormGroup.valid && this.isExternalFormValid);
          } else {
            this.formValid$.next(this.cardFormGroup.valid);
          }
        }));

      this.setupStripeElements();

    } else {
      this.formValid$.next(true);
    }

    if (this.paymentButtonClicked) {
      this.subscriptions.add(this.paymentButtonClicked
        .subscribe(amount => {
          this.startLoggedPayment(amount);
        }));
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  setupStripeElements() {
    // Sets styles for Stripe Elements
    const invalid = '#eb5757';

    const style = {
      base: {
        color: '#5a6170',
        fontWeight: 400,
        fontFamily: '\'Roboto\', sans-serif',
        fontSize: '16px',
        lineHeight: '35px',
        '::placeholder': {
          color: '#d1d5de',
        },
      },
      invalid: {
        color: invalid,
      },
    };

    // Adds parameters to Stripe Elements
    this.elementStripe = this.STRIPE.elements({
      locale: this.translate.currentLang,
      fonts: [
        {
          cssSrc:
            'https://fonts.googleapis.com/css2?family=Alegreya+Sans:wght@500&display=swap',
        },
      ],
    });

    // Adds input properties to Stripe Elements
    const cardNumber = this.elementStripe.create('cardNumber', {
      placeholder: 'XXXX XXXX XXXX XXXX',
      style,
      classes: {
        base: 'input-stripe-custom'
      },
    });
    const cardExp = this.elementStripe.create('cardExpiry', {
      placeholder: 'MM/AA',
      style,
      classes: {
        base: 'input-stripe-custom'
      },
    });
    const cardCvc = this.elementStripe.create('cardCvc', {
      placeholder: '***',
      style,
      classes: {
        base: 'input-stripe-custom'
      },
    });

    // Builds stripe element input (iframe) inside the div with the corresponding html id
    cardNumber.mount('#card-number');
    cardExp.mount('#expiration-date');
    cardCvc.mount('#cvc-number');

    this.cardNumber = cardNumber;
    this.cardExp = cardExp;
    this.cardCvc = cardCvc;

    // This way we can hear the stripe elements event and act on the form
    this.cardNumber.addEventListener('change', this.onFormControlChanges.bind(this, this.CARD_NUMBER_FIELD_NAME));
    this.cardExp.addEventListener('change', this.onFormControlChanges.bind(this, this.CARD_EXP_FIELD_NAME));
    this.cardCvc.addEventListener('change', this.onFormControlChanges.bind(this, this.CARD_CVC_FIELD_NAME));

  }

  private onFormControlChanges(formControlName: any, event: ElementChangeEventModel) {
    switch (formControlName) {
      case this.CARD_NUMBER_FIELD_NAME:
        this.cardNumberError = event?.error?.message ? event.error.message : null;
        break;
      case this.CARD_EXP_FIELD_NAME:
        this.expirationDateError = event?.error?.message ? event.error.message : null;
        break;
      case this.CARD_CVC_FIELD_NAME:
        this.cvcError = event?.error?.message ? event.error.message : null;
        break;
    }
    this.cardFormGroup.patchValue({ [formControlName]: (!event?.error && !event.empty) });
  }

  private endPaymentProcess(status: string): void {
    this.isLoading = false;
    this.paymentStatus$.emit(status);
  }

}
